import { Controller } from "@hotwired/stimulus";
import {
  getSchedulingPupil,
  setSchedulingPupil,
  clearSchedulingPupil,
  SCHEDULING_PUPIL_CHANGED_EVENT,
} from "../../utils/scheduling-pupil";

export default class extends Controller {
  static targets = ["input", "panel", "list", "schedulingChip", "schedulingName"];
  static values = {
    url: String,
    extraParamSelectors: Object,
    focusShortcut: Boolean,
    scheduling: Boolean,
  };

  onDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hidePanel();
    }
  }

  onFocus() {
    if (this.inputTarget.value.trim()) {
      this.scheduleSearch();
    }
  }

  search() {
    this.scheduleSearch();
  }

  scheduleSearch() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.performSearch(), 300);
  }

  close() {
    this.hidePanel();
  }

  async performSearch() {
    const q = this.inputTarget.value.trim();
    if (!q) {
      this.hidePanel();
      return;
    }

    const params = new URLSearchParams({ query: q });
    this.appendExtraParams(params);
    const url = `${this.urlValue}?${params}`;
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      if (!response.ok) {
        this.hidePanel();
        return;
      }
      const data = await response.json();
      this.renderResults(data);
    } catch (_e) {
      this.hidePanel();
    }
  }

  renderResults(data) {
    const results = data.results || [];
    const viewMoreUrl = data.view_more_url || "/schools/pupils";

    this.listTarget.innerHTML = "";

    if (results.length === 0) {
      const empty = document.createElement("p");
      empty.className = "DriveatoNavPupilSearch-empty  m0  p1  u-textMuted";
      empty.textContent = "No pupils found";
      this.listTarget.appendChild(empty);
      this.showPanel();
      this.appendViewMore(viewMoreUrl);
      return;
    }

    results.forEach((row) => {
      const a = document.createElement("a");
      a.href = row.url;
      a.className =
        "DriveatoNavPupilSearch-result  u-linkClean  u-block  p1";
      a.dataset.turbo = "false";

      const title = document.createElement("div");
      title.className = "u-medium";
      title.textContent = row.name;
      a.appendChild(title);

      if (row.subtitle) {
        const sub = document.createElement("div");
        sub.className = "u-textSmall  u-textMuted";
        sub.textContent = row.subtitle;
        a.appendChild(sub);
      }

      if (this.schedulingValue && row.id) {
        a.classList.add("DriveatoNavPupilSearch-result--withAction");
        a.appendChild(this.buildSchedulingButton(row));
      }

      this.listTarget.appendChild(a);
    });

    this.appendViewMore(viewMoreUrl);
    this.showPanel();
  }

  appendViewMore(viewMoreUrl) {
    const wrap = document.createElement("div");
    wrap.className = "DriveatoNavPupilSearch-footer  u-borderTop";

    const a = document.createElement("a");
    a.href = viewMoreUrl;
    a.className =
      "DriveatoNavPupilSearch-viewMore  u-linkClean  u-block  p1  u-textSmall  u-medium";
    a.textContent = "View more";
    a.dataset.turbo = "false";
    wrap.appendChild(a);

    this.listTarget.appendChild(wrap);
  }

  showPanel() {
    this.panelTarget.classList.remove("u-hidden");
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  hidePanel() {
    this.panelTarget.classList.add("u-hidden");
    this.listTarget.innerHTML = "";
    this.inputTarget.setAttribute("aria-expanded", "false");
  }

  buildSchedulingButton(row) {
    const isActive = String(getSchedulingPupil()?.id) === String(row.id);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "Button  Button--outline  Button--small  Button--slim  DriveatoNavPupilSearch-scheduleButton";
    button.classList.toggle("is-active", isActive);
    button.title = isActive
      ? "Stop scheduling for this student"
      : "Schedule this student into open slots";
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
    button.innerHTML = '<span class="icon  icon-calendar  u-inlineBlock" aria-hidden="true"></span>';
    button.addEventListener("click", (event) => {
      // The button sits inside the result link — don't navigate to the student.
      event.preventDefault();
      event.stopPropagation();
      this.toggleScheduling(row);
    });
    return button;
  }

  toggleScheduling(row) {
    if (String(getSchedulingPupil()?.id) === String(row.id)) {
      clearSchedulingPupil();
    } else {
      setSchedulingPupil({ id: row.id, name: row.name });
    }
    this.inputTarget.value = "";
    this.hidePanel();
  }

  clearScheduling(event) {
    event.preventDefault();
    clearSchedulingPupil();
  }

  renderSchedulingChip() {
    if (!this.hasSchedulingChipTarget) return;
    const pupil = getSchedulingPupil();
    this.schedulingChipTarget.classList.toggle("u-hidden", !pupil);
    this.element.classList.toggle("is-scheduling", !!pupil);
    if (pupil) this.schedulingNameTarget.textContent = pupil.name;
  }

  // '/' keyboard shortcut
  connect() {
    this.boundDocClick = this.onDocumentClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);

    document.addEventListener("click", this.boundDocClick);
    document.addEventListener("keydown", this.boundKeydown);

    if (this.schedulingValue) {
      this.boundSchedulingChanged = this.renderSchedulingChip.bind(this);
      document.addEventListener(SCHEDULING_PUPIL_CHANGED_EVENT, this.boundSchedulingChanged);
      this.renderSchedulingChip();
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocClick);
    document.removeEventListener("keydown", this.boundKeydown);
    if (this.boundSchedulingChanged) {
      document.removeEventListener(SCHEDULING_PUPIL_CHANGED_EVENT, this.boundSchedulingChanged);
    }
    clearTimeout(this.timeout);
  }

  onKeydown(e) {
    if (this.hasFocusShortcutValue && !this.focusShortcutValue) return;

    const active = document.activeElement;
    const isTyping =
      ["INPUT", "TEXTAREA"].includes(active.tagName) ||
      active.isContentEditable;

    if (isTyping) return;

    if (e.key === "/") {
      e.preventDefault();
      this.focusInput();
    }
  }

  focusInput() {
    this.inputTarget.focus();
    this.inputTarget.select();
  }

  appendExtraParams(params) {
    if (!this.hasExtraParamSelectorsValue) return;

    Object.entries(this.extraParamSelectorsValue).forEach(([paramName, selector]) => {
      const field = document.querySelector(selector);
      if (field && field.value) {
        params.append(paramName, field.value);
      }
    });
  }
}
